import React from "react";
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton,
  Menu, MenuItem, TextField, TablePagination, Box,
  Button
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

/* ---------- Value Parsing for Sorting ---------- */
function parseValue(value) {
  // Check Date or Timestamp (ISO string or epoch ms)
  if (
    (typeof value === "string" && !isNaN(Date.parse(value))) ||
    (typeof value === "number" && new Date(value).getTime() > 0)
  ) {
    return new Date(value).getTime();
  }

  // Check numeric/decimal
  const n = parseFloat(value);
  if (!isNaN(n)) return n;

  // Default to string
  return value.toString();
}

function compareValues(a, b) {
  const vA = parseValue(a);
  const vB = parseValue(b);

  if (typeof vA === "number" && typeof vB === "number") return vA - vB;
  return vA.toString().localeCompare(vB.toString(), undefined, { numeric: true });
}

function descendingComparator(a, b, orderBy) {
  return compareValues(b[orderBy], a[orderBy]);
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

export default function CustomMuiTable({headerColumns, rows, dataFormatter, filterColumns, sortingColumns, defaultSortField, defaultSortOrder}) {
  const columns = Object.keys(headerColumns);
  let filterCol ={};
  columns?.map((col)=>{
    filterCol = {...filterCol, [col]: ""};
    return {[col]: ""};
  });

  
  const [order, setOrder] = React.useState(defaultSortOrder ? defaultSortOrder :"asc");
  const [orderBy, setOrderBy] = React.useState(defaultSortField ? defaultSortField : columns[0]);
  const [filters, setFilters] = React.useState(filterCol);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [activeCol, setActiveCol] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  // Sorting
  const handleSort = (col) => {
    const isAsc = orderBy === col && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(col);
  };

  // Filter menu handlers
  const openMenu = (e, col) => {
    setAnchorEl(e.currentTarget);
    setActiveCol(col);
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setActiveCol("");
  };

  const filtered = rows?.filter((r) =>
    columns.every((c) =>{
        
        return r?.[c] ? r?.[c]?.toString()?.toLowerCase()?.includes(filters?.[c]?.toLowerCase()) : true;
    }
      
    )
  ); 
  
  const clearAllFilters = () => setFilters(filterCol);
  const visibleRows = filtered
    .sort(getComparator(order, orderBy))
    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  return (
    
    <>
      <TableContainer className="table-container">
        {/* Clear Filters Button */}
      <Box sx={{ textAlign: "right", mt: 1 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={clearAllFilters}
          disabled={!Object.values(filters).some(Boolean)}
        >
          Clear All Filters
        </Button>
      </Box>
        <Table className="table">
            <TableHead className="table-head">
                <TableRow className="table-row">
              {columns.map((col) => (
                <TableCell key={col}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between"
                    }}
                  >
                    {/* Column label with up/down arrow */}
                    <Box
                      onClick={() => sortingColumns && sortingColumns?.includes(col) ? handleSort(col) : null}
                      sx={{ display: "flex", alignItems: "center", cursor: sortingColumns && sortingColumns?.includes(col) ? "pointer" : "arrow" }}
                    >
                      <strong>{headerColumns[col]}</strong>
                      {orderBy === col ? (
                        order === "asc" ? (
                          <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        ) : (
                          <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        )
                      ) : null}
                    </Box>

                    {/* 3-dot filter icon */}
                    {filterColumns && filterColumns?.includes(col) && (
                      <IconButton size="small" onClick={(e) => openMenu(e, col)}>
                        <MoreVertIcon fontSize="inherit" />
                      </IconButton>
                    )}
                    
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody className="table-body">
            {visibleRows.map((row, i) => (
              <TableRow key={i} hover className={`table-row`}>
                {dataFormatter(row, columns)}
              </TableRow>
            ))}
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No matching records
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 30, 40, 50]}
        labelRowsPerPage="Show records"
      />

      {/* Filter Menu (per column) */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <MenuItem disableRipple sx={{ p: 1.5 }}>
          <TextField
            size="small"
            autoFocus
            placeholder={`Filter ${activeCol}`}
            value={filters[activeCol] || ""}
            onChange={(e) =>
              setFilters({ ...filters, [activeCol]: e.target.value })
            }
          />
        </MenuItem>
      </Menu>
    </>
  );
}
